import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { 
  Shield, 
  Clock, 
  Star, 
  FileText, 
  Users, 
  Award,
  Target,
  Heart,
  ChevronRight
} from 'lucide-react';

export const AboutPage = () => {
  const { t } = useLanguage();

  const values = [
    { icon: Shield, ...t('about.values.security') },
    { icon: Clock, ...t('about.values.punctuality') },
    { icon: Star, ...t('about.values.excellence') },
    { icon: Heart, ...t('about.values.commitment') },
  ];

  const milestones = t('about.history.milestones');

  const team = [
    {
      name: 'Carlos Mendoza',
      role: 'Director General',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80',
    },
    {
      name: 'María González',
      role: 'Directora de Operaciones',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=400&q=80',
    },
    {
      name: 'Roberto Silva',
      role: 'Jefe de Flota',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=400&q=80',
    },
  ];

  return (
    <div data-testid="about-page" className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 noise-overlay" />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <span className="text-sm tracking-widest uppercase text-sky-400 font-medium mb-4 block">
              {t('about.sectionLabel')}
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              {t('about.title')}{' '}
              <span className="text-gradient">{t('about.titleHighlight')}</span>
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              {t('about.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-16">
            <div className="card-service">
              <div className="w-14 h-14 bg-sky-100 rounded-xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-sky-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('about.mission.title')}</h2>
              <p className="text-slate-500 leading-relaxed">
                {t('about.mission.description')}
              </p>
            </div>

            <div className="card-service">
              <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mb-6">
                <Award className="w-7 h-7 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('about.vision.title')}</h2>
              <p className="text-slate-500 leading-relaxed">
                {t('about.vision.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-sm tracking-widest uppercase text-sky-600 font-medium mb-4 block">
              {t('about.values.sectionLabel')}
            </span>
            <h2 className="section-title mb-4">
              {t('about.values.title')}
            </h2>
            <p className="section-subtitle max-w-2xl mx-auto">
              {t('about.values.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                data-testid={`value-card-${index}`}
                className="card-service text-center"
              >
                <div className="w-16 h-16 bg-sky-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-sky-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-500 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* History / Timeline */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm tracking-widest uppercase text-sky-600 font-medium mb-4 block">
                {t('about.history.sectionLabel')}
              </span>
              <h2 className="section-title mb-6">
                {t('about.history.title')}
              </h2>
              <p className="text-slate-500 leading-relaxed mb-8">
                {t('about.history.description')}
              </p>

              <div className="space-y-4">
                {milestones.map((milestone, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                  >
                    <div className="w-16 h-16 bg-sky-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold">{milestone.year}</span>
                    </div>
                    <span className="text-slate-700 font-medium">{milestone.event}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1560179707-f14e90ef3623?auto=format&fit=crop&w=800&q=80"
                  alt="Oficinas FastCar"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-sm tracking-widest uppercase text-sky-600 font-medium mb-4 block">
              {t('about.team.sectionLabel')}
            </span>
            <h2 className="section-title mb-4">
              {t('about.team.title')}
            </h2>
            <p className="section-subtitle max-w-2xl mx-auto">
              {t('about.team.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div
                key={index}
                data-testid={`team-member-${index}`}
                className="card-service text-center"
              >
                <div className="w-32 h-32 rounded-full overflow-hidden mx-auto mb-6 ring-4 ring-slate-100">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-1">
                  {member.name}
                </h3>
                <p className="text-sky-600 font-medium">
                  {member.role}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Billing Highlight */}
      <section className="py-16 bg-emerald-50 border-y border-emerald-100">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 bg-emerald-500 rounded-2xl flex items-center justify-center">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-1">
                  {t('about.billing.title')}
                </h3>
                <p className="text-slate-600">
                  {t('about.billing.description')}
                </p>
              </div>
            </div>
            <Link to="/contacto#reservar">
              <Button 
                data-testid="btn-reserve-billing"
                className="btn-primary"
              >
                {t('about.billing.cta')}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section-padding bg-slate-900">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { icon: Users, value: '200+', label: t('about.stats.clients') },
              { icon: Award, value: '10+', label: t('about.stats.experience') },
              { icon: Star, value: '4.9', label: t('about.stats.rating') },
              { icon: FileText, value: '100%', label: t('about.stats.invoiced') },
            ].map((stat, index) => (
              <div key={index} data-testid={`about-stat-${index}`}>
                <stat.icon className="w-10 h-10 text-sky-400 mx-auto mb-4" />
                <div className="text-4xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-slate-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-white">
        <div className="container-custom text-center">
          <h2 className="section-title mb-6">
            {t('about.ctaTitle')}
          </h2>
          <p className="section-subtitle max-w-2xl mx-auto mb-8">
            {t('about.ctaSubtitle')}
          </p>
          <Link to="/contacto#reservar">
            <Button 
              data-testid="btn-cta-about"
              className="btn-accent text-lg px-12 py-5"
            >
              {t('about.ctaButton')}
              <ChevronRight className="w-6 h-6 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
